/** Ids are UUIDs; anything else cannot address a conversation and must not reach a route. */
const SAFE_ID = /^[A-Za-z0-9-]+$/;

export interface NotificationRouteInput {
  subType?: string;
  data?: unknown;
}

/**
 * Where a notification takes you, or null when it takes you nowhere.
 *
 * A bubble that announces a reply and cannot be acted on leaves the reader
 * holding a fact and a navigation problem: open the menu, find WhatsApp, find
 * the conversation among the others. By then the point of announcing it
 * instantly is gone.
 *
 * Null is a real answer, not a failure. A bubble that looks tappable and does
 * nothing is worse than plain text, so the caller renders it inert instead —
 * which is also what keeps this honest as new subtypes appear without
 * destinations.
 */
export function toNotificationRoute({ subType, data }: NotificationRouteInput): string | null {
  if (subType !== 'whatsapp_message_received') return null;

  if (!data || typeof data !== 'object') return null;

  const conversationId = (data as { conversationId?: unknown }).conversationId;
  // The payload crosses the wire as JSON and is not ours to trust: a value that
  // is not a plain id could walk out of the path it is interpolated into.
  if (typeof conversationId !== 'string' || !SAFE_ID.test(conversationId)) return null;

  return `/whatsapp/${conversationId}`;
}
